/**
 * ThreadHeaderLabel — the "you are here" affordance (thread-workflow UX T2).
 *
 * Renders the ACTIVE thread's title in the chat header's subtitle slot so the
 * user always knows which conversation the pane is showing — in every view
 * (floating / docked / fullscreen), on every page. Click to rename inline
 * (Enter/blur commits, Escape cancels); renaming stamps `userRenamed`, which
 * every labeler/backfill path already respects permanently.
 *
 * The rename is the viewer's OWN name for the thread — nobody else's list
 * changes (PR-B, 2026-09-09) — so it rides `threads.organize-own` (catalog
 * v13). Without the key the label still names the thread and still says which
 * conversation this is; it simply is not a button (PR-1's rule: never a
 * control that fails after the click). `canRename` ABSENT reads as true.
 */
import React, { useEffect, useState } from 'react';
import { ThreadHeaderLabelButton, ThreadHeaderRenameInput } from './styles';

export interface ThreadHeaderLabelProps {
  title: string;
  onRename: (title: string) => void;
  /** Whether this viewer may rename a thread for themselves; absent ⇒ yes. */
  canRename?: boolean;
  /**
   * Phones only (2026-09-13): tapping the title opens the thread list.
   *
   * The chip strip is retired at phone width — a horizontally-scrolling rail
   * that showed about one and a half chips on a 390px screen, for ~48px of a
   * transcript already getting less than half. The thread NAME is the natural
   * place to go looking for the other threads, it is the largest target in the
   * header, and the list it opens is the same ThreadListSheet the strip's own
   * overflow trigger opened — search, new thread, and per-row rename included.
   *
   * Rename therefore moves to that sheet on a phone rather than disappearing,
   * which is the docked strip's argument one screen down: the surface goes,
   * every affordance it carried stays reachable. Absent ⇒ click renames,
   * exactly as before, at every other width.
   */
  onOpenThreadList?: () => void;
}

export const ThreadHeaderLabel: React.FC<ThreadHeaderLabelProps> = ({ title, onRename, canRename, onOpenThreadList }) => {
  const mayRename = canRename !== false;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);

  // Track external title changes (AI label landing, cross-device rename)
  // while NOT clobbering an in-progress edit.
  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  const commit = () => {
    const clean = draft.trim();
    setEditing(false);
    if (clean && clean !== title) onRename(clean);
  };

  // Before the rename branches: opening the list needs no permission, so a
  // viewer without `threads.organize-own` still gets a working switcher rather
  // than the plain <span> below.
  if (onOpenThreadList) {
    return (
      <ThreadHeaderLabelButton
        type="button"
        data-thread-header-label=""
        data-thread-header-opens-list=""
        aria-haspopup="dialog"
        title="all threads"
        aria-label={`current thread: ${title} — show all threads`}
        onClick={onOpenThreadList}
      >
        <span data-thread-header-title="">{title}</span>
      </ThreadHeaderLabelButton>
    );
  }

  if (editing && mayRename) {
    return (
      <ThreadHeaderRenameInput
        value={draft}
        autoFocus
        data-thread-header-rename=""
        aria-label="rename this thread"
        maxLength={42}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setDraft(title);
            setEditing(false);
          }
        }}
      />
    );
  }

  if (!mayRename) {
    return (
      <ThreadHeaderLabelButton
        as="span"
        data-thread-header-label=""
        aria-label={`current thread: ${title}`}
      >
        <span data-thread-header-title="">{title}</span>
      </ThreadHeaderLabelButton>
    );
  }

  return (
    <ThreadHeaderLabelButton
      type="button"
      data-thread-header-label=""
      title="click to rename this thread"
      aria-label={`current thread: ${title} — click to rename`}
      onClick={() => setEditing(true)}
    >
      <span data-thread-header-title="">{title}</span>
    </ThreadHeaderLabelButton>
  );
};

export default ThreadHeaderLabel;
